"use strict";

import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  CHARACTER_NAME_MAX_LENGTH,
  characterWriteErrorMessage,
  renameCharacter,
} from "./renameCharacter.js";

const FILTER_THRESHOLD = 12;

export const getCharacterKey = (character, index) => {
  return (
    character.speaker ||
    character.id ||
    character.characterId ||
    character.name ||
    `character-${index}`
  );
};

export const getCharacterLabel = (character) => {
  return (
    character.display_name ||
    character.displayName ||
    character.speaker ||
    "Unnamed Character"
  );
};

const CharacterSelector = ({
  projectId,
  characters,
  selectedKeys,
  onSelectionChange,
  allowMultiple,
  onCharacterRenamed,
}) => {
  const [filterText, setFilterText] = useState("");
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [savingSpeaker, setSavingSpeaker] = useState(null);

  const characterEntries = useMemo(() => {
    return characters.map((character, index) => ({
      character,
      key: getCharacterKey(character, index),
    }));
  }, [characters]);

  const visibleEntries = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return characterEntries;

    return characterEntries.filter(({ character }) =>
      getCharacterLabel(character).toLowerCase().includes(query)
    );
  }, [characterEntries, filterText]);

  const allKeys = characterEntries.map(({ key }) => key);
  const allSelected =
    allKeys.length > 0 && selectedKeys.length === allKeys.length;
  const someSelected =
    selectedKeys.length > 0 && selectedKeys.length < allKeys.length;

  const toggleCharacter = (key) => {
    if (!allowMultiple) {
      onSelectionChange(selectedKeys.includes(key) ? [] : [key]);
      return;
    }

    onSelectionChange(
      selectedKeys.includes(key)
        ? selectedKeys.filter((selectedKey) => selectedKey !== key)
        : [...selectedKeys, key]
    );
  };

  const startEditing = (character) => {
    setEditingSpeaker(character.speaker);
    setDraftName(character.displayName || "");
  };

  const cancelEditing = () => {
    setEditingSpeaker(null);
    setDraftName("");
  };

  const saveName = async (character) => {
    const nextName = draftName.trim();

    if (!nextName) {
      toast.error("Character name cannot be empty.");
      return;
    }

    if (nextName === character.displayName) {
      cancelEditing();
      return;
    }

    try {
      setSavingSpeaker(character.speaker);

      const renamed = await renameCharacter({
        projectId,
        speaker: character.speaker,
        name: nextName,
      });

      if (!renamed) {
        toast.error("Character no longer exists on this project.");
        return;
      }

      onCharacterRenamed(character.speaker, nextName);
      toast.success(`Renamed to "${nextName}".`);
      cancelEditing();
    } catch (error) {
      console.error("Failed to rename character:", error);
      toast.error(
        characterWriteErrorMessage(error, "Error updating character name")
      );
    } finally {
      setSavingSpeaker(null);
    }
  };

  const renderTile = ({ character, key }) => {
    const isSelected = selectedKeys.includes(key);
    const isEditing = !!character.speaker && editingSpeaker === character.speaker;
    const isSaving = !!character.speaker && savingSpeaker === character.speaker;

    const tileSx = {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      minWidth: 0,
      minHeight: 46,
      pl: allowMultiple ? 0.5 : 1.5,
      pr: 0.5,
      borderRadius: "12px",
      border:
        isSelected || isEditing ? "1px solid #1f6f96" : "1px solid #dfe6ee",
      background: isSelected
        ? "linear-gradient(135deg, rgba(31,111,150,0.14), rgba(255,255,255,0.96))"
        : "#f9fafb",
      transition: "all 160ms ease",
      boxSizing: "border-box",
    };

    if (isEditing) {
      return (
        <Box key={key} sx={{ ...tileSx, pl: 1.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            value={draftName}
            placeholder={character.speaker}
            disabled={isSaving}
            inputProps={{ maxLength: CHARACTER_NAME_MAX_LENGTH }}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveName(character);
              if (event.key === "Escape") cancelEditing();
            }}
          />

          {isSaving ? (
            <CircularProgress size={18} sx={{ mx: 1 }} />
          ) : (
            <>
              <Tooltip title="Save">
                <IconButton
                  size="small"
                  onClick={() => saveName(character)}
                  sx={{ color: "#1f6f96" }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={cancelEditing}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      );
    }

    return (
      <Box
        key={key}
        role={allowMultiple ? "checkbox" : "radio"}
        aria-checked={isSelected}
        tabIndex={0}
        onClick={() => toggleCharacter(key)}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleCharacter(key);
          }
        }}
        sx={{
          ...tileSx,
          cursor: "pointer",
          outline: "none",
          "&:hover, &:focus-visible": {
            borderColor: "#1f6f96",
          },
          "& .character-rename-button": {
            opacity: 0,
            transition: "opacity 120ms ease",
          },
          "&:hover .character-rename-button, &:focus-within .character-rename-button":
            {
              opacity: 1,
            },
          "@media (hover: none)": {
            "& .character-rename-button": { opacity: 1 },
          },
        }}
      >
        {allowMultiple && (
          <Checkbox
            size="small"
            checked={isSelected}
            tabIndex={-1}
            sx={{ p: 0.75 }}
          />
        )}

        <Typography
          variant="body2"
          title={character.speaker}
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: isSelected ? 750 : 650,
            color: isSelected ? "#0f2f46" : "#1f2937",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getCharacterLabel(character)}
        </Typography>

        {!!character.projectId && (
          <span
            title="Imported Character"
            style={{ fontSize: "var(--font-size-normal)" }}
          >
            ↙️
          </span>
        )}

        {!!character.speaker && (
          <Tooltip title="Rename character">
            <span>
              <IconButton
                className="character-rename-button"
                size="small"
                aria-label={`Rename ${getCharacterLabel(character)}`}
                disabled={!!savingSpeaker}
                onClick={(event) => {
                  event.stopPropagation();
                  startEditing(character);
                }}
                onKeyDown={(event) => event.stopPropagation()}
                sx={{ color: "#1f6f96" }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 850, color: "#101828" }}
          >
            {allowMultiple ? "Characters" : "Character"}
          </Typography>

          <Typography variant="body2" sx={{ color: "#667085" }}>
            {selectedKeys.length > 0
              ? `${selectedKeys.length} selected`
              : allowMultiple
              ? "Select one or more"
              : "Select one"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {characters.length > FILTER_THRESHOLD && (
            <TextField
              size="small"
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              placeholder="Filter characters"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 200,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#f9fafb",
                },
              }}
            />
          )}

          {allowMultiple && characters.length > 0 && (
            <Box
              component="label"
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 650,
                color: "#1f2937",
              }}
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => onSelectionChange(allSelected ? [] : allKeys)}
              />
              Select all
            </Box>
          )}
        </Box>
      </Box>

      {characters.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#667085" }}>
          No characters found
        </Typography>
      ) : visibleEntries.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#667085" }}>
          No characters match "{filterText.trim()}"
        </Typography>
      ) : (
        <Box
          role={allowMultiple ? "group" : "radiogroup"}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 1,
            maxHeight: 264,
            overflowY: "auto",
            p: 0.25,
          }}
        >
          {visibleEntries.map(renderTile)}
        </Box>
      )}
    </Box>
  );
};

export default CharacterSelector;
